import React, { useEffect, useState } from 'react';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import PillToggle from 'src/components/controls/PillToggle';
import { useForm } from 'react-hook-form';
import DialogList from 'src/dialogs';

interface IClientNote {
    id?: number;
    date?: string;
    by?: string;
    text: string;
    active?: boolean;
    createdDate?: string;
    deactiveDate?: string;
    noteTypeDescription?: string;
}

interface IClientNotesDialogProps {
    dialogName: string;
    hideDialog: () => void;
    showDialog?: (dialogName: DialogList) => void;
    returnDialogName?: DialogList;
    clientNumber?: number | string;
    clientName?: string;
    notes?: Array<IClientNote>;
    handleAddNote?: (text: string) => void;
    handleUpdateNote?: (noteId: number, text: string) => void;
    handleToggleActive?: (noteId: number, active: boolean) => void;
}

type EntryMode = 'none' | 'add' | 'edit';

// Folder-tab dimensions for the "New Note"/"Edit Note" header - the diagonal cut's horizontal
// run equals its vertical run (TAB_HEIGHT) so the angle comes out to a true 45 degrees.
const TAB_HEIGHT = 32;
const TAB_WIDTH = 140;
const NOTE_TEXTAREA_HEIGHT = 96;

const ClientNotesDialog = (props: IClientNotesDialogProps) => {
    const [notes, setNotes] = useState<Array<IClientNote>>(props.notes || []);
    const [entryMode, setEntryMode] = useState<EntryMode>('none');
    const [editingNoteId, setEditingNoteId] = useState<number | undefined>(undefined);
    const { register, getValues, reset, watch, formState } = useForm();
    const { isDirty } = formState;
    const noteTextLength = (watch('noteText') || '').length;

    useEffect(() => {
        setNotes(props.notes || []);
    }, [props.notes]);

    const AddNoteClicked = () => {
        setEntryMode('add');
        setEditingNoteId(undefined);
        reset({ noteText: '' });
    };

    const EditNoteClicked = (note: IClientNote) => {
        if (note.id === undefined) {
            return;
        }

        setEntryMode('edit');
        setEditingNoteId(note.id);
        reset({ noteText: note.text });
    };

    const SaveClicked = () => {
        // Not dirty means the text is unchanged from whatever it was seeded with (blank for
        // Add, the original note for Edit) - nothing to persist, OK just closes the entry.
        if (isDirty) {
            const text = getValues()?.noteText?.trim();

            if (text) {
                if (entryMode === 'edit' && editingNoteId !== undefined) {
                    setNotes((prev) => prev.map((n) => (n.id === editingNoteId ? { ...n, text } : n)));
                    props.handleUpdateNote?.(editingNoteId, text);
                } else {
                    // Prepend, not append - the list is newest-first, and the parent's own
                    // post-save refresh (server-ordered, full metadata) will replace this
                    // stub shortly after anyway, so this is just the transient state.
                    setNotes((prev) => [{ text, date: new Date().toLocaleString(), active: true }, ...prev]);
                    props.handleAddNote?.(text);
                }
            }
        }

        setEntryMode('none');
        setEditingNoteId(undefined);
        reset({ noteText: '' });
    };

    const CancelNoteClicked = () => {
        setEntryMode('none');
        setEditingNoteId(undefined);
        reset({ noteText: '' });
    };

    const ReturnClicked = () => {
        reset({ noteText: '' });

        if (props.returnDialogName && props.showDialog) {
            props.showDialog(props.returnDialogName);
        } else {
            props.hideDialog();
        }
    };

    const toggleNoteActive = (note: IClientNote) => {
        if (note.id === undefined) {
            return;
        }

        const nowActive = !(note.active !== false);

        setNotes((prev) => prev.map((n) => (n.id === note.id ? {
            ...n,
            active: nowActive,
            deactiveDate: nowActive ? undefined : new Date().toLocaleString()
        } : n)));

        props.handleToggleActive?.(note.id, nowActive);
    };

    return (
        <Dialog title="A/R Client Notes" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '30%' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <form style={{ width: '100%' }}>
                    {!!props.clientNumber && (
                        <label>
                            {props.clientName ? `Client: ${props.clientName} (${props.clientNumber})` : `Client # ${props.clientNumber}`}
                        </label>
                    )}
                    <div style={{ maxHeight: '12rem', overflowY: 'auto', border: '1px solid #ccc', padding: '0.5rem', marginTop: '0.5rem' }}>
                        {notes.length === 0 && <div style={{ color: '#888' }}>No notes on file for this client.</div>}
                        {notes.map((note, index) => (
                            <div
                                key={note.id ?? index}
                                onDoubleClick={() => EditNoteClicked(note)}
                                style={{ marginBottom: '0.75rem', paddingBottom: '0.75rem', borderBottom: '1px solid black', cursor: 'pointer' }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    {/* Mirrors the legacy SET_STATUS trigger: green when active, red when inactive. */}
                                    <div style={{ fontSize: '0.9rem', color: note.active !== false ? 'green' : 'red' }}>Created By: {note.by} {note.date}</div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                        <div style={{ fontSize: '0.9rem', color: '#333' }}>Status:</div>
                                        <PillToggle
                                            checked={note.active !== false}
                                            title={note.active !== false ? 'Active - click to deactivate' : 'Inactive - click to activate'}
                                            onChange={() => toggleNoteActive(note)}
                                        />
                                    </div>
                                </div>
                                <div style={{ fontSize: '1.05rem' }}>{note.text}</div>
                                <div style={{ display: 'flex', gap: '1rem', fontSize: '0.85rem', marginTop: '0.15rem' }}>
                                    {note.createdDate && <span style={{ color: 'green' }}>{note.createdDate}</span>}
                                    {note.deactiveDate && <span style={{ color: 'maroon' }}>{note.deactiveDate}</span>}
                                    {note.noteTypeDescription && <span style={{ color: '#333' }}>{note.noteTypeDescription}</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                    {entryMode !== 'none' && (
                        <>
                        <div
                            style={{
                                position: 'relative',
                                marginTop: '0.5rem',
                                background: 'white',
                                border: '1px solid #ccc',
                                height: `${TAB_HEIGHT + NOTE_TEXTAREA_HEIGHT}px`,
                                clipPath: `polygon(0 0, ${TAB_WIDTH - TAB_HEIGHT}px 0, ${TAB_WIDTH}px ${TAB_HEIGHT}px, 100% ${TAB_HEIGHT}px, 100% 100%, 0 100%)`
                            }}
                        >
                            <div
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: `${TAB_WIDTH}px`,
                                    height: `${TAB_HEIGHT}px`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    paddingLeft: '0.6rem',
                                    boxSizing: 'border-box',
                                    fontWeight: 'bold',
                                    fontSize: '1.05rem',
                                    color: '#555'
                                }}
                            >
                                {entryMode === 'edit' ? 'Edit Note' : 'New Note'}
                            </div>
                            <textarea
                                name="noteText"
                                ref={register}
                                maxLength={500}
                                style={{
                                    position: 'absolute',
                                    top: `${TAB_HEIGHT}px`,
                                    left: 0,
                                    width: '100%',
                                    height: `${NOTE_TEXTAREA_HEIGHT}px`,
                                    boxSizing: 'border-box',
                                    border: 'none',
                                    outline: 'none',
                                    resize: 'none',
                                    background: 'transparent',
                                    padding: '0.5rem',
                                    fontFamily: 'inherit',
                                    fontSize: '0.95rem'
                                }}
                            />
                        </div>
                        <div
                            style={{
                                textAlign: 'right',
                                fontSize: '0.75rem',
                                color: noteTextLength >= 500 ? 'red' : '#888'
                            }}
                        >
                            {noteTextLength}/500
                        </div>
                        </>
                    )}
                </form>
            </div>
            <div className="form-row" style={{ marginTop: '2rem', justifyContent: 'center' }}>
                {entryMode === 'none' ? (
                    <>
                    <Button
                        className="blue-hover-button"
                        style={{ width: '24%', marginRight: '1rem' }}
                        text={'Add Note'}
                        onClick={() => AddNoteClicked()}
                    />
                    <Button
                        className="blue-hover-button"
                        style={{ width: '24%', marginRight: '1rem' }}
                        text={'Cancel'}
                        onClick={() => ReturnClicked()}
                    />
                    </>
                ) : (
                    <>
                    <Button
                        className="blue-hover-button"
                        style={{ width: '24%', marginRight: '1rem' }}
                        text={isDirty ? 'Save Note' : 'OK'}
                        onClick={() => SaveClicked()}
                    />
                    <Button
                        className="blue-hover-button"
                        style={{ width: '24%', marginRight: '1rem' }}
                        text={entryMode === 'edit' ? 'Cancel Edit' : 'Cancel Note'}
                        onClick={() => CancelNoteClicked()}
                    />
                    </>
                )}
            </div>
        </Dialog>
    );
};

export default ClientNotesDialog;



// WEBPACK FOOTER //
// ./src/components/accounts-receivable/dialogs/ClientNotesDialog.tsx